import { EventCollector, MockEventCollector } from "./EventCollector";
import {
  CFG_GENERIC,
  CFG_ENDPOINTS,
  getDomainAndPort,
} from "../config_files/config.js";
import { fetchConfigValues } from "./utils/ConfigFetch";

export { EventCollectorLoader, InfoproductConfiguration };

interface InfoproductConfiguration {
  CFG_ENDPOINTS: CfgEndpoints;
  infoproductConfigValues?: Record<string, string>;
  businessUnitConfigValues?: BusinessUnitConfigValues;
  // Ad-network click capture definitions aggregated by api-core from
  // marketing's registry (null when marketing was unavailable — ClickIds
  // falls back to its built-in list)
  clickCaptureDefs?: import("./utils/ClickIds").ClickCaptureDefsDocument | null;
  // Píxeles de navegador de la BU: manifest materializado por api-marketing
  // (GET /browser-pixel-manifest) y agregado por api-core igual que
  // clickCaptureDefs. `null` = marketing no respondió al agregar; ausente =
  // la BU no tiene módulo de ads o api-core aún no lo publica. En ninguno de
  // los dos casos el SDK inyecta nada: no hay default seguro para un píxel.
  browserPixels?:
    | import("./pixels/BrowserPixelAdapter").BrowserPixelsManifest
    | null;
}

interface BusinessUnitConfigValues {
  gtag_manager_id?: string;
  // Raw JSON string keyed by marketing domain hostname; see
  // resolveCustomGtmLoader in utils/TagManager.ts
  gtm_custom_loaders?: string;
  push_notifications_enabled?: string;
  push_notifications_key?: string;
  push_notifications_provider?: string;
  related_crm_team_id?: string;
}

export interface Headers {
  "Content-Type": string;
}

export interface CfgEndpoints {
  LeadCreationEndpoint: string;
  WebAnalyticsEventEndpoint: string;
  CalendlyScheduledEventEndpoint: string;
  VslServingEndpoint: string;
}

class EventCollectorLoader {
  private _mockEventCollector: MockEventCollector;

  constructor(mockEventCollector: MockEventCollector) {
    this._mockEventCollector = mockEventCollector;
  }

  async init(): Promise<EventCollector> {
    const crmInfoproductId = this._mockEventCollector.crmInfoproductId;

    if (!crmInfoproductId) {
      throw new Error("crmInfoproductId are required parameters");
    }

    if (isNaN(crmInfoproductId)) {
      throw new Error("crmInfoproductId must be valid numbers");
    }

    console.log("Starting EventCollectorLoader initialization");

    const protodealInfoproductId = this._mockEventCollector.protodealInfoproductId;
    const protodealBusinessUnitId = this._mockEventCollector.protodealBusinessUnitId;

    const configValues: InfoproductConfiguration =
      await this._getConfigValues(protodealInfoproductId, protodealBusinessUnitId);
    configValues.CFG_ENDPOINTS = CFG_ENDPOINTS;

    const eventCollector = new EventCollector(this._mockEventCollector);
    await eventCollector.init(configValues, []);

    return new Promise((resolve) => {
      resolve(eventCollector);
    });
  }

  isDev(): boolean {
    let response = false;
    const domainAndPort = getDomainAndPort();
    const domain = domainAndPort.split(":")[0];

    if (domain !== "hub.protodeal.com") {
      // TODO: revisar esta lógica para determinar isDev
      response = true;
    }

    return response;
  }

  private async _getConfigValues(protodealInfoproductId: number, protodealBusinessUnitId: number): Promise<any> {
    // First-party + retry con backoff + cache stale de localStorage: el config
    // era un punto único de fallo (si muere al cargar, el collector no se
    // inicializa y el lead nunca se envía). Ver ConfigFetch.ts (W2.6).
    return fetchConfigValues(
      CFG_GENERIC.configValuesUrl,
      protodealInfoproductId,
      protodealBusinessUnitId,
    );
  }

}
